import { Box } from '@quintoandar-tokko/jopijs';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DEFAULT_STACK_NAME = 'stack1';

export default function GenericBarChart({
  labels,
  datasets,
  stacked = true,
  onBarClick,
  getTooltipLabel,
  yMax,
  yTicksStepSize,
}) {
  const normalizedLabels = labels.map((label) => {
    if (typeof label === 'string') return label;
    return label.axisLabel || label.name;
  });

  const chartData = {
    labels: normalizedLabels,
    datasets: datasets.map((dataset) => ({
      ...dataset,
      label: dataset.label,
      data: dataset.data,
      backgroundColor: dataset.backgroundColor,
      hoverBackgroundColor: dataset.backgroundColor,
      stack: stacked ? DEFAULT_STACK_NAME : undefined,
      maxBarThickness: 100,
    })),
  };

  function handleHover(event, elements) {
    if (!elements.length) return;

    const { index } = elements[0];
    const clickedLabel = labels[index];
    const isNonClickable = clickedLabel?.nonClickable;
    const showPointer = onBarClick && !isNonClickable;
    event.native.target.style.cursor = showPointer ? 'pointer' : 'default';
  }

  function handleClick(_, elements) {
    if (!elements.length) return;
    const { datasetIndex, index } = elements[0];
    const clickedLabel = labels[index];

    if (clickedLabel?.nonClickable) return;

    const dataset = chartData.datasets[datasetIndex];
    const value = dataset.data[index];

    if (onBarClick) {
      onBarClick({ dataset, label: clickedLabel, value, datasetIndex, index });
    }
  }

  function handleTooltip(context) {
    if (typeof getTooltipLabel === 'function') {
      return getTooltipLabel(context);
    }

    const label = context.dataset.label || '';
    const value = context.raw;
    return `${label}: ${value}`;
  }

  function handleXAxisLabel(value) {
    const maxXAxisLabelLength = 12;
    // eslint-disable-next-line
    const label = this.getLabelForValue(value);
    if (typeof label === 'string' && label.length > maxXAxisLabelLength) {
      return `${label.substring(0, maxXAxisLabelLength)}...`;
    }
    return label;
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      title: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: handleTooltip,
        },
      },
    },
    scales: {
      x: {
        stacked,
        ticks: {
          callback: handleXAxisLabel,
          maxRotation: 0,
          autoSkip: false,
        },
      },
      y: {
        stacked,
        ...(yMax !== undefined ? { max: yMax } : {}),
        ticks: {
          // eslint-disable-next-line
          callback: function (value) {
            if (Number.isInteger(Number(value))) {
              return value;
            }
            return '';
          },
          ...(yTicksStepSize !== undefined ? { stepSize: yTicksStepSize } : {}),
        },
        stepSize: 1,
      },
    },
    onHover: handleHover,
    onClick: handleClick,
  };

  return (
    <Box __css={{ height: '100%' }}>
      <Bar options={options} data={chartData} />
    </Box>
  );
}
